import React from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import ReferralShareCard from "../components/ReferralShareCard";
import { whatsappOrderUrl } from "../components/Nav";

const EASE = [0.22, 1, 0.36, 1];

function CheckMark() {
  return (
    <motion.div
      initial={{ scale: 0.6, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.5, ease: EASE }}
      className="w-16 h-16 rounded-full bg-rw-mint flex items-center justify-center mb-6"
      data-testid="thankyou-check"
    >
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path d="M5 13l4 4L19 7" stroke="#1A1A24" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </motion.div>
  );
}

export default function ThankYou() {
  const location = useLocation();
  const state = location.state || {};
  const isOrder = state.kind === "order" && state.order;
  const isSub = state.kind === "subscription" && state.subscription;

  const order = state.order;
  const sub = state.subscription;
  const customerName = order?.customer_name || sub?.customer_name || "";

  return (
    <div className="min-h-screen bg-rw-bg text-rw-ink" data-testid="thankyou-page">
      <Seo
        title="Thank You — Order Received"
        description="Thank you for choosing RIZ N WASH. Your pickup is confirmed — we'll WhatsApp you shortly."
        path="/thank-you"
        noindex={true}
      />
      <Nav />

      <div className="max-w-4xl mx-auto px-6 md:px-10 py-16 md:py-20">
        <CheckMark />
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: EASE, delay: 0.1 }}
        >
          <div className="overline mb-3">Thank you{customerName ? `, ${customerName.split(" ")[0]}` : ""}</div>
          <h1 className="font-display text-4xl md:text-6xl font-light tracking-tight leading-[1.05]" data-testid="thankyou-heading">
            {isSub ? "You're all set." : isOrder ? "Your pickup is scheduled." : "We've got your order."}
          </h1>
          <p className="mt-4 text-rw-ink2 max-w-2xl">
            {isSub
              ? "Your weekly plan is active. We'll WhatsApp you the night before every pickup — cancel anytime."
              : "We'll message you on WhatsApp shortly to confirm. Our runner will arrive in your chosen slot."}
          </p>
        </motion.div>

        {isOrder && (
          <div className="mt-10 grid md:grid-cols-5 gap-6">
            <div className="md:col-span-3 rounded-2xl border border-rw-ink/10 bg-rw-bg2 p-6" data-testid="thankyou-details">
              <div className="overline">Order code</div>
              <div className="font-display text-3xl md:text-4xl mt-1" data-testid="thankyou-order-code">{order.order_code}</div>
              <div className="grid grid-cols-2 gap-4 mt-6 text-sm">
                <div>
                  <div className="overline">Pickup</div>
                  <div className="mt-1">{order.pickup_date}</div>
                  <div className="text-rw-ink2">{order.pickup_slot}</div>
                </div>
                <div>
                  <div className="overline">Customer</div>
                  <div className="mt-1">{order.customer_name}</div>
                  <div className="text-rw-ink2">{order.phone}</div>
                </div>
                <div className="col-span-2">
                  <div className="overline">Address</div>
                  <div className="mt-1">{order.address}{order.sector ? `, ${order.sector}` : ""}</div>
                </div>
              </div>
              <div className="mt-6 border-t border-rw-ink/10 pt-4">
                {order.items.map((it) => (
                  <div key={it.service_id} className="flex justify-between text-sm py-1">
                    <span>{it.name} <span className="text-rw-ink3">× {it.qty} {it.unit.split(" ").slice(1).join(" ")}</span></span>
                    <span>₹{(it.qty * it.unit_price).toFixed(0)}</span>
                  </div>
                ))}
                <div className="flex justify-between text-sm py-1 text-rw-ink2">
                  <span>Pickup & delivery</span>
                  <span>{order.pickup_delivery_fee > 0 ? `₹${order.pickup_delivery_fee}` : "Free"}</span>
                </div>
                <div className="flex justify-between font-display text-2xl py-2 border-t border-rw-ink/10 mt-2">
                  <span>Total</span>
                  <span data-testid="thankyou-total">₹{order.total}</span>
                </div>
              </div>
            </div>

            <div className="md:col-span-2 rounded-2xl border border-rw-ink/10 bg-rw-ink text-white p-6" data-testid="thankyou-next">
              <div className="overline text-white/60">What happens next</div>
              <ol className="mt-4 space-y-3 text-sm text-white/85 list-none">
                <li className="flex gap-3"><span className="font-display text-rw-blue">01</span> We WhatsApp you a confirmation within minutes.</li>
                <li className="flex gap-3"><span className="font-display text-rw-blue">02</span> Runner collects your bag in the {order.pickup_slot} slot.</li>
                <li className="flex gap-3"><span className="font-display text-rw-blue">03</span> Clothes washed, ironed & quality-checked.</li>
                <li className="flex gap-3"><span className="font-display text-rw-blue">04</span> Delivered folded to your door — pay via UPI or cash.</li>
              </ol>
              <div className="mt-6 flex flex-col gap-2">
                <Link to="/track" className="text-center rounded-full py-2.5 text-sm font-semibold bg-white text-rw-ink" data-testid="thankyou-track-btn">
                  Track order {order.order_code}
                </Link>
                <a
                  href={whatsappOrderUrl(`Hi RIZ N WASH! Question about my order ${order.order_code}.`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-center rounded-full py-2.5 text-sm font-semibold border border-white/30"
                  data-testid="thankyou-whatsapp-btn"
                >
                  Message us on WhatsApp
                </a>
              </div>
            </div>
          </div>
        )}

        {isSub && (
          <div className="mt-10 rounded-2xl border border-rw-ink/10 bg-rw-bg2 p-6" data-testid="thankyou-sub-details">
            <div className="overline">Subscription code</div>
            <div className="font-display text-3xl mt-1" data-testid="thankyou-sub-code">{sub.sub_code}</div>
            <div className="grid grid-cols-2 gap-4 mt-6 text-sm">
              <div><div className="overline">Plan</div><div className="mt-1">{sub.plan_name}</div></div>
              <div><div className="overline">Weekly</div><div className="mt-1">₹{sub.weekly_total} <span className="text-rw-ink3 line-through">₹{sub.weekly_price}</span></div></div>
              <div><div className="overline">Starts</div><div className="mt-1">{sub.start_date}</div></div>
              <div><div className="overline">Your referral code</div><div className="font-mono mt-1" data-testid="thankyou-sub-referral">{state.referral_code}</div></div>
            </div>
          </div>
        )}

        {!isOrder && !isSub && (
          <div className="mt-10 rounded-2xl border border-rw-ink/10 bg-rw-bg2 p-6" data-testid="thankyou-generic">
            <p className="text-rw-ink2">
              If you've just placed an order, we'll confirm on WhatsApp shortly. Need help right away?
              We're one message away.
            </p>
          </div>
        )}

        <div className="mt-8 flex flex-wrap gap-3">
          <a
            href={whatsappOrderUrl(isOrder ? `Hi RIZ N WASH! Question about my order ${order.order_code}.` : undefined)}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-dark rounded-full px-6 py-3 text-sm font-semibold"
            data-testid="thankyou-whatsapp-cta"
          >
            Chat on WhatsApp →
          </a>
          <Link to="/" className="rounded-full px-6 py-3 border border-rw-ink/20 text-sm font-semibold hover:bg-rw-ink hover:text-white transition-colors" data-testid="thankyou-home-btn">
            Back home
          </Link>
        </div>

        {state.referral_code && (
          <ReferralShareCard code={state.referral_code} name={customerName} />
        )}
      </div>

      <Footer />
    </div>
  );
}
